import React from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const Contact = () => {
  return (
    <section id="contact" className="py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em]">
              Get In Touch
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-4 tracking-tight">
            Contact Us
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            We're here to help you 24/7. Reach out to us anytime!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="lux-card rounded-2xl">
              <CardContent className="p-8">
                <h3 className="font-display text-2xl font-semibold text-white mb-6">Quick Contact</h3>
                
                <div className="space-y-6">
                  {/* Phone */}
                  <a href="tel:+917507867648" className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full flex items-center justify-center transition-colors">
                      <Phone className="w-6 h-6 text-[#d4af37]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Phone</h4>
                      <p className="text-[#d4af37] font-semibold text-lg group-hover:underline">+91 75078 67648</p>
                      <p className="text-sm text-gray-500">Available 24/7</p>
                    </div>
                  </a>

                  {/* WhatsApp */}
                  <a href="https://wa.me/917507867648" target="_blank" rel="noopener noreferrer" className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full flex items-center justify-center transition-colors">
                      <svg className="w-6 h-6 text-[#d4af37]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">WhatsApp</h4>
                      <p className="text-[#d4af37] font-semibold text-lg group-hover:underline">Chat with us</p>
                      <p className="text-sm text-gray-500">Quick response guaranteed</p>
                    </div>
                  </a>

                  {/* Email */}
                  <a href="mailto:sgcabs.official@gmail.com" className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full flex items-center justify-center transition-colors">
                      <Mail className="w-6 h-6 text-[#d4af37]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Email</h4>
                      <p className="text-[#d4af37] font-semibold group-hover:underline">sgcabs.official@gmail.com</p>
                      <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                    </div>
                  </a>

                  {/* Location */}
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#d4af37]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Location</h4>
                      <p className="text-gray-300">Bangalore, Karnataka</p>
                      <p className="text-sm text-gray-500">Serving all of Bangalore & Outstation</p>
                    </div>
                  </div>

                  {/* Operating Hours */}
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-teal-500/10 border border-teal-500/30 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Operating Hours</h4>
                      <p className="text-gray-200 font-semibold">24/7 Service</p>
                      <p className="text-sm text-gray-500">Always available for your needs</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map */}
          <div>
            <Card className="h-full lux-card rounded-2xl overflow-hidden">
              <CardContent className="p-0 h-full min-h-[600px] bg-[#0a0a0a]">
                <iframe
                  data-testid="contact-map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.0516707688243!2d77.71449631482181!3d12.960509090868989!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae13a19e54156b%3A0xc35ed95745c1a8e6!2sSG%20Cabs!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{
                    border: 0,
                    minHeight: '600px',
                    display: 'block',
                    filter: 'invert(92%) hue-rotate(180deg) brightness(0.85) contrast(0.95) saturate(0.6) sepia(0.25)',
                  }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SG Cabs Location"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
